import { Position } from 'geojson';
import { produce } from 'immer';
import { backendApi } from 'state/services/backend/api';
import { BackendTag } from 'state/services/backend/enums';
import { ProjectIdRequest, ProjectPayloadRequest } from 'state/services/backend/types/general/request';
import { SetProjectGeneralSettingsRequest } from 'state/services/backend/types/project/city-view/request';
import {
  ChangeProjectSettingsRequest,
  CreateProjectRequest,
  GetProjectUtilizationByDtoRequest,
  SetConfidenceLevelRequest,
  SetLawZoneRequest,
} from 'state/services/backend/types/project/request';
import {
  CreateProjectResponse,
  GetProjectResponse,
  ProjectSia416Areas,
  ProjectVolumes,
} from 'state/services/backend/types/project/response';
import { IProjectUtilization } from 'state/services/backend/types/project/utilization';
import { RequestMethod } from 'types/request';

export const projectApiSlice = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    createProject: builder.mutation<CreateProjectResponse, CreateProjectRequest>({
      query: (body) => ({
        url: `/api/project`,
        method: RequestMethod.POST,
        body,
      }),
      invalidatesTags: [BackendTag.PROJECT, BackendTag.CREDITS],
    }),
    getProject: builder.query<GetProjectResponse, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project/${projectId}`,
        method: RequestMethod.GET,
      }),
      transformResponse: (response: GetProjectResponse) => {
        // TODO: Remove this transformation once the backend returns the polygon in the correct format
        return produce(response, (draft) => {
          const projectPolygon = draft.location.plot.polygon as unknown as Position[];
          draft.location.plot.polygon = [projectPolygon];
        });
      },
      providesTags: [BackendTag.PROJECT],
    }),
    changeProjectSettings: builder.mutation<void, ChangeProjectSettingsRequest>({
      query: ({ projectId, name, description }) => ({
        url: `/api/project/${projectId}/change-settings`,
        method: RequestMethod.PUT,
        body: { name, description },
      }),
      invalidatesTags: [BackendTag.PROJECT],
    }),
    getProjectSiaAreas: builder.query<ProjectSia416Areas, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project-areas/${projectId}/sia416`,
        method: RequestMethod.GET,
      }),
      providesTags: [BackendTag.PROJECT_AREAS],
    }),
    getProjectVolumes: builder.query<ProjectVolumes, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project/${projectId}/volumes`,
        method: RequestMethod.GET,
      }),
      providesTags: [BackendTag.PROJECT_VOLUMES],
    }),
    getProjectUtilization: builder.query<IProjectUtilization, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project-utilization/${projectId}`,
        method: RequestMethod.GET,
      }),
      providesTags: [BackendTag.UTILIZATION],
    }),
    getProjectUtilizationByDto: builder.query<
      IProjectUtilization,
      ProjectPayloadRequest<GetProjectUtilizationByDtoRequest>
    >({
      query: ({ projectId, payload }) => ({
        url: `/api/project-utilization/${projectId}/by-dto`,
        method: RequestMethod.POST,
        body: payload,
      }),
    }),
    setConfidenceLevel: builder.mutation<void, SetConfidenceLevelRequest>({
      query: ({ projectId, confidenceLevel }) => ({
        url: `/api/project/${projectId}/confidence-level/${confidenceLevel}`,
        method: RequestMethod.PUT,
      }),
      invalidatesTags: [BackendTag.PROJECT, BackendTag.ENERGY],
    }),
    setLawZone: builder.mutation<void, SetLawZoneRequest>({
      query: ({ projectId, zoneId }) => ({
        url: `/api/project/${projectId}/law-zone/${zoneId}`,
        method: RequestMethod.PUT,
      }),
      invalidatesTags: [
        BackendTag.PROJECT,
        BackendTag.PROJECT_RULES_CHECK,
        BackendTag.USER_PROJECT_RULES,
        BackendTag.DATA_OVERVIEW,
      ],
    }),
    setProjectGeneralSettings: builder.mutation<void, ProjectPayloadRequest<SetProjectGeneralSettingsRequest>>({
      query: ({ projectId, payload }) => ({
        url: `/api/project/${projectId}/general-settings`,
        method: RequestMethod.POST,
        payload,
      }),
    }),
  }),
});

export const {
  useCreateProjectMutation,
  useGetProjectQuery,
  useChangeProjectSettingsMutation,
  useGetProjectSiaAreasQuery,
  useGetProjectUtilizationQuery,
  useGetProjectUtilizationByDtoQuery,
  useGetProjectVolumesQuery,
  useSetConfidenceLevelMutation,
  useSetLawZoneMutation,
} = projectApiSlice;

export const useGetProjectQueryState = projectApiSlice.endpoints.getProject.useQueryState;

export const useGetProjectSiaAreasQueryState = projectApiSlice.endpoints.getProjectSiaAreas.useQueryState;

export const useGetProjectVolumesQueryState = projectApiSlice.endpoints.getProjectVolumes.useQueryState;
